// BodySilhouette.tsx — V6
// Athena-themed anatomical body silhouettes using Catmull-Rom spline paths.
// Four variants: male, female, kid, neutral. Kid renders visually smaller.
// Periwinkle stroke matching navbar icon aesthetic + optional neon glow.

import React, { useMemo } from "react";

export type SilhouetteVariant = "neutral" | "male" | "female" | "kid";

interface Props {
  variant?: SilhouetteVariant;
  className?: string;
  style?: React.CSSProperties;
  /** When true, uses a subtle fill instead of neon outline (for light backgrounds) */
  lightMode?: boolean;
  /**
   * Stroke color override — pass the theme's secondary.main (periwinkle)
   * to match navbar icon styling. Defaults to gradient if not provided.
   */
  strokeColor?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Catmull-Rom spline → cubic Bezier SVG path (closed loop)
// ─────────────────────────────────────────────────────────────────────────────

type Point = [number, number];

function splinePath(pts: Point[]): string {
  const n = pts.length;
  if (n < 3) return "";
  let d = `M ${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)} `;
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];
    const p3 = pts[(i + 2) % n];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += `C ${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)} `;
  }
  return d + "Z";
}

// ─────────────────────────────────────────────────────────────────────────────
// Body control-point arrays (viewBox 0 0 100 150)
// ─────────────────────────────────────────────────────────────────────────────

const MALE: Point[] = [
  [48.5,2.5],[50,3],[52.5,3],[55,4.5],[56.5,7],[57.5,10.5],[58,14],
  [57.5,17.5],[56.5,20],[55.5,22],[55,23],[55.5,25],
  [59,26.5],[63,28],[67,29.5],[71,32],[73,35],
  [72,38],[72,42],[71.5,46],[71,50],[71,54],[70.5,58],[70,62],
  [70,65],[70,68],[69.5,71],[69.5,74],[69,77],
  [67,77],[66,74],[66,71],[65.5,68],[65.5,65],[65.5,62],[66,58],[66,54],
  [66,50],[66.5,46],[66.5,42],[67,38],
  [66.5,35],[65,33],[65,36],[65,40],[64.5,46],[64,52],[63.5,57],[63,60],
  [63,64],[63,68],
  [65,72],[65,77],[64.5,83],[64,89],[63.5,95],[63,101],[63,107],[63,113],[62.5,119],[61.5,125],[60.5,130],
  [59.5,134],[59.5,137],[60,140],[61,143],[62,145.5],[62.5,147],[60,148],[56.5,148],
  [54,147.5],[53.5,145.5],[53,142],[52.5,138],[52.5,134],[52,129],[52,123],[51.5,117],[51.5,111],[51.5,105],[51.5,99],[51.5,94],[51.5,89],[51.5,83],[51.5,77],[51.5,72],
  [51,70],[50,68.5],[49,70],
  [48.5,72],[48.5,77],[48.5,83],[48.5,89],[48.5,94],[48.5,99],[48.5,105],[48.5,111],[48.5,117],[48,123],[48,129],[47.5,134],[47.5,138],[47,142],[46.5,145.5],[46,147.5],
  [43.5,148],[40,148],[37.5,147],[38,145.5],[39,143],[40,140],[40.5,137],[40.5,134],
  [40,130],[38.5,125],[37.5,119],[37,113],[37.5,107],[37,101],[36.5,95],[36,89],[35.5,83],[35,77],[35,72],
  [37,68],[37,64],[37,60],[36.5,57],[36,52],[35.5,46],[35,40],[35,36],
  [35,33],[33.5,35],[33,38],[33.5,42],[33.5,46],[34,50],[34,54],[34,58],[34.5,62],[34.5,65],[34.5,68],[34,71],[34,74],[33,77],
  [31,77],[30.5,74],[30.5,71],[30,68],[30,65],[30,62],[29.5,58],[29,54],[29,50],[28.5,46],[28,42],[28,38],
  [27,35],[29,32],[33,29.5],[37,28],[41,26.5],
  [44.5,25],[45,23],[44.5,22],[43.5,20],[42.5,17.5],[42,14],[42.5,10.5],[43.5,7],[45,4.5],[47.5,3],
];

const FEMALE: Point[] = [
  [50,3.5],[53,3],[56,4.5],[58,7],[59,10.5],[59.5,14.5],
  [59.5,18],[61,21],[62,24],[62.5,26.5],[62,28.5],
  [64,30],[66,31],[67.5,32.5],[69,35],
  [69.5,38],[70,42],[70,46],[70,50],[69.5,54],[69,58],[68.5,62],[68,65],[68,68],[68,71],[67.5,74],[66.5,74.5],
  [65.5,74],[65.5,71],[65.5,68],[66,65],[66,62],[66,58],[66.5,54],[66.5,50],[66.5,46],[66,42],[65.5,38],
  [65,35.5],[64,33.5],
  [63,35],[63,37.5],[63,40],[63,43],[63,45],[62.5,47.5],[62,50],[62,52.5],[62,55.5],
  [62.5,58],[63.5,61],[64.5,64],[65.5,67],
  [67,71],[66,77],[65,83],[64,89],[63,95],[61.5,101],[61,107],[61,113],[60.5,119],[59.5,125],[58.5,130],
  [58,134],[58,137],[58.5,140],[59,142.5],[60,144.5],[60.5,146],[58.5,147],[55.5,147],
  [53,146.5],[52.5,145],[52.5,142],[52,138],[52,134],[51.5,129],[51.5,123],[51.5,117],[51.5,111],[51.5,105],[51.5,99],[51.5,94],[51.5,89],[51.5,83],[51,77],[51,72],
  [50.5,70],[50,68.5],[49.5,70],
  [49,72],[49,77],[48.5,83],[48.5,89],[48.5,94],[48.5,99],[48.5,105],[48.5,111],[48.5,117],[48.5,123],[48.5,129],[48,134],[48,138],[47.5,142],[47,145],[46.5,146.5],
  [44.5,147],[41.5,147],[39.5,146],[40,144.5],[41,142.5],[41.5,140],[42,137],[42,134],
  [41,130],[40,125],[39,119],[38.5,113],[38.5,107],[38,101],[37,95],[36,89],[35,83],[34,77],[33,71],
  [34.5,67],[35.5,64],[36.5,61],[37.5,58],[38,55.5],
  [38,52.5],[38,50],[37.5,47.5],[37,45],[37,43],[37,40],[37,37.5],[37,35],
  [36,33.5],[35,35.5],
  [34.5,38],[34,42],[33.5,46],[33.5,50],[33.5,54],[34,58],[34,62],[34,65],[34.5,68],[34.5,71],[34.5,74],[33.5,74.5],
  [32.5,74],[32,71],[32,68],[32,65],[31.5,62],[31,58],[30.5,54],[30,50],[30,46],[30,42],[30.5,38],
  [31,35],[32.5,32.5],[34,31],[36,30],
  [38,28.5],[37.5,26.5],[38,24],[39,21],[40.5,18],
  [40.5,14.5],[41,10.5],[42,7],[44,4.5],[47,3],
];

const KID: Point[] = [
  [49,2.5],[51,2],[53.5,2.5],
  [56,3],[58,4],[59.5,5.5],[60.5,5],[61,7],[61.5,10],[61.5,14],[61,18],
  [60,21],[59,24],[57.5,26],[56,27.5],[55.5,29],
  [58,30],[60.5,31],[63,32.5],[65,34.5],
  [65.5,37],[66,40],[66,44],[66,48],[65.5,52],[65,56],[65,59],[65,62],[65,65],[64.5,67],
  [63.5,67],[63,65],[63,62],[63,59],[63,56],[63.5,52],[63.5,48],[63.5,44],[63,40],[63,37.5],
  [62.5,35.5],[61.5,34.5],[61.5,37],[61.5,42],[61.5,48],[61,54],[61,58],[60.5,63],[60.5,68],
  [60,73],[59.5,78],[59,84],[58.5,90],[58,96],[57.5,102],[57.5,108],[57,114],[57,120],[56.5,126],[56,131],
  [56,135],[56,138],[56.5,141],[57.5,143],[58.5,144.5],[58.5,146],[57,147],[54.5,147],
  [53,146.5],[52.5,145],[52,142],[52,138],[52,135],[51.5,130],[51.5,124],[51.5,118],[51.5,112],[51.5,106],[51.5,100],[51.5,94],[51.5,88],[51.5,82],[51.5,77],[51,72],
  [51,69.5],[50,68],[49,69.5],
  [49,72],[48.5,77],[48.5,82],[48.5,88],[48.5,94],[48.5,100],[48.5,106],[48.5,112],[48.5,118],[48.5,124],[48.5,130],[48,135],[48,138],[47.5,142],[47.5,145],[47,146.5],
  [45.5,147],[43,147],[41.5,146],[41.5,144.5],[42.5,143],[43.5,141],[44,138],[44,135],
  [44,131],[43.5,126],[43,120],[43,114],[42.5,108],[42.5,102],[42,96],[41.5,90],[41,84],[40.5,78],[40,73],
  [39.5,68],[39.5,63],[39,58],[39,54],[38.5,48],[38.5,42],[38.5,37],
  [38.5,34.5],[37.5,35.5],[37,37.5],[37,40],[36.5,44],[36.5,48],[36.5,52],[37,56],[37,59],[37,62],[37,65],[36.5,67],
  [35.5,67],[35,65],[35,62],[35,59],[35,56],[34.5,52],[34,48],[34,44],[34,40],[34.5,37],
  [35,34.5],[37,32.5],[39.5,31],[42,30],
  [44,29],[44,27.5],[42.5,26],[41,24],[40,21],[39,18],[38.5,14],[38.5,10],
  [39,7],[39.5,5],[40.5,5.5],[42,4],[44,3],[46.5,2.5],[47.5,2],
];

const NEUTRAL: Point[] = [
  [50,3],[52,2.5],[54,4],[56,6.5],[57,10],[57.5,14],[57.5,18],
  [57,20.5],[55.5,22.5],[55,24],[55,25],[55.5,27],
  [59,28],[63,29.5],[67,31.5],[70,34],
  [70.5,37],[71,41],[71,45],[71,50],[70.5,54],[70,58],[70,62],[69.5,65],[69.5,68],[69.5,71],[69,74],[68,75],
  [66,74],[66,71],[65.5,68],[65.5,65],[65.5,62],[65.5,58],[65.5,54],[65.5,50],[66,45],[66,41],[66.5,37],
  [66,34.5],[64.5,33],[64.5,36],[64.5,40],[64,46],[63.5,52],[63,57],[62.5,60],
  [63,64],[63,68],
  [65,72],[64.5,77],[64,83],[63.5,89],[63,95],[62.5,101],[62.5,107],[62.5,113],[62,119],[61,125],[60,130],
  [59.5,134],[59.5,137],[60,140],[61,142.5],[62,144.5],[62,146],[60,147],[56.5,147],
  [53.5,146.5],[53,145],[52.5,142],[52,138],[52,134],[51.5,129],[51.5,123],[51.5,117],[51.5,111],[51.5,105],[51.5,99],[51.5,94],[51.5,89],[51.5,83],[51.5,77],[51.5,72],
  [51,70],[50,68.5],[49,70],
  [48.5,72],[48.5,77],[48.5,83],[48.5,89],[48.5,94],[48.5,99],[48.5,105],[48.5,111],[48.5,117],[48.5,123],[48.5,129],[48,134],[48,138],[47.5,142],[47,145],[46.5,146.5],
  [43.5,147],[40,147],[38,146],[38,144.5],[39,142.5],[40,140],[40.5,137],[40.5,134],
  [40,130],[39,125],[38,119],[37.5,113],[38,107],[37.5,101],[37,95],[36.5,89],[36,83],[35.5,77],[35,72],
  [37,68],[37,64],[37.5,60],[37,57],[36.5,52],[36,46],[35.5,40],[35.5,36],
  [35.5,33],[34,34.5],[33.5,37],[34,41],[34,45],[34.5,50],[34.5,54],[34.5,58],[34.5,62],[34.5,65],[34.5,68],[34,71],[34,74],[32,75],
  [31,74],[31,71],[30.5,68],[30.5,65],[30,62],[30,58],[29.5,54],[29,50],[29,45],[29,41],[29.5,37],
  [30,34],[33,31.5],[37,29.5],[41,28],
  [44.5,27],[45,25],[45,24],[44.5,22.5],[43,20.5],[42.5,18],[42.5,14],[43,10],[44,6.5],[46,4],[48,2.5],
];

const BODIES: Record<SilhouetteVariant, Point[]> = { male: MALE, female: FEMALE, kid: KID, neutral: NEUTRAL };

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const BodySilhouette: React.FC<Props> = ({ variant = "neutral", className, style, lightMode, strokeColor }) => {
  const pathD = useMemo(() => splinePath(BODIES[variant]), [variant]);

  // Kid renders at 75% scale, centered
  const isKid = variant === "kid";
  const kidTransform = isKid ? "translate(50,75) scale(0.75) translate(-50,-75)" : undefined;

  // Unique IDs for SVG defs (avoid clashes when multiple instances)
  const uid = useMemo(() => `bs-${Math.random().toString(36).slice(2, 8)}`, []);

  // Use explicit strokeColor if provided (navbar style), otherwise gradient
  const useGradient = !strokeColor;

  return (
    <svg
      viewBox="0 0 100 150"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ width: "100%", height: "100%", ...style }}
    >
      <defs>
        {useGradient && (
          <>
            {/* Neon Outline gradient: periwinkle → royal blue */}
            <linearGradient id={`${uid}-g`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={lightMode ? "#4466cc" : "#88aaff"} />
              <stop offset="50%" stopColor={lightMode ? "#3355aa" : "#5577ff"} />
              <stop offset="100%" stopColor={lightMode ? "#2244aa" : "#3355cc"} />
            </linearGradient>
          </>
        )}
        {/* Subtle glow filter */}
        <filter id={`${uid}-f`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur in="SourceGraphic" stdDeviation={lightMode ? "0.5" : "1.0"} result="a" />
          <feGaussianBlur in="SourceGraphic" stdDeviation={lightMode ? "1.2" : "2.5"} result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="a" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g transform={kidTransform}>
        {/* Very transparent periwinkle fill for the body interior */}
        <path
          d={pathD}
          fill={strokeColor || "#b7c1de"}
          fillOpacity={0.08}
          stroke="none"
        />
        {/* Outline stroke with glow */}
        <path
          d={pathD}
          fill="none"
          stroke={useGradient ? `url(#${uid}-g)` : strokeColor}
          strokeWidth={lightMode ? 1.2 : 1.4}
          strokeLinecap="round"
          strokeLinejoin="round"
          filter={`url(#${uid}-f)`}
        />
      </g>
    </svg>
  );
};

export default BodySilhouette;
